import React, { useState, useMemo, useEffect } from 'react';
import {
  Container, Typography, Grid, Card, CardContent, CardActionArea, Box,
  Button, TextField, InputAdornment, Chip, Select, MenuItem, FormControl,
  InputLabel, Dialog, DialogTitle, DialogContent, DialogActions,
  Autocomplete, Switch, FormControlLabel, LinearProgress,
} from '@mui/material';
import {
  Add, Search, FolderOpen, People, CalendarToday, AttachMoney,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useProjects } from '../context_providers/ProjectContext';
import { useNotification } from '../context_providers/NotificationContext';
import { ProjectStatus, Client } from '../../types';
import apiService from '../../utils/api.service';
import LoadingSpinner from '../shared/LoadingSpinner';

const PROJECT_COLORS = [
  '#7b6df6', '#10b981', '#3b82f6', '#f59e0b', '#ef4444',
  '#ec4899', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316',
];

const STATUS_OPTIONS: { label: string; value: ProjectStatus | 'all' }[] = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Archived', value: 'archived' },
  { label: 'Completed', value: 'completed' },
];

const STATUS_COLORS: Record<ProjectStatus, 'success' | 'default' | 'info'> = {
  active: 'success',
  archived: 'default',
  completed: 'info',
};

interface NewProjectForm {
  name: string;
  code: string;
  clientId: string;
  description: string;
  color: string;
  isBillable: boolean;
  hourlyRate: string;
  budgetHours: string;
}

const emptyForm: NewProjectForm = {
  name: '', code: '', clientId: '', description: '',
  color: PROJECT_COLORS[0], isBillable: true, hourlyRate: '', budgetHours: '',
};

const generateCode = (name: string): string => {
  return name
    .split(/\s+/)
    .map(w => w.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 5);
};

const ProjectsPage: React.FC = () => {
  const navigate = useNavigate();
  const { projects, clients, isLoading, createProject } = useProjects();
  const { showSuccess, showError } = useNotification();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | 'all'>('all');
  const [clientFilter, setClientFilter] = useState<string>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<NewProjectForm>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [budgetMap, setBudgetMap] = useState<Record<string, { usedHours: number; percentUsed: number }>>({});

  useEffect(() => {
    const fetchBudgets = async () => {
      try {
        const resp = await apiService.getBudgetReport();
        const budgets = resp?.data?.budgets || resp?.budgets || [];
        const map: Record<string, { usedHours: number; percentUsed: number }> = {};
        budgets.forEach((b: any) => {
          map[b.projectId] = { usedHours: b.usedHours || 0, percentUsed: b.percentUsed || 0 };
        });
        setBudgetMap(map);
      } catch {
        // Budget data is supplementary; fail silently
      }
    };
    if (projects.length > 0) fetchBudgets();
  }, [projects]);

  const clientMap = useMemo(() => {
    const map: Record<string, Client> = {};
    clients.forEach(c => { map[c.id] = c; });
    return map;
  }, [clients]);

  const filteredProjects = useMemo(() => {
    return projects.filter(p => {
      const matchesSearch = !searchQuery ||
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.code.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
      const matchesClient = clientFilter === 'all' || p.clientId === clientFilter;
      return matchesSearch && matchesStatus && matchesClient;
    });
  }, [projects, searchQuery, statusFilter, clientFilter]);

  const handleNameChange = (name: string) => {
    setForm(prev => ({
      ...prev,
      name,
      code: prev.code === generateCode(prev.name) || !prev.code ? generateCode(name) : prev.code,
    }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return;
    setSubmitting(true);
    try {
      await createProject({
        name: form.name.trim(),
        code: form.code.trim() || generateCode(form.name),
        clientId: form.clientId || undefined,
        description: form.description.trim() || undefined,
        color: form.color,
        isBillable: form.isBillable,
        hourlyRate: form.hourlyRate || undefined,
        budgetHours: form.budgetHours || undefined,
      });
      showSuccess('Project created successfully');
      setForm(emptyForm);
      setDialogOpen(false);
    } catch (err: any) {
      showError(err.message || 'Failed to create project');
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) return <LoadingSpinner message="Loading projects..." />;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Projects</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            {projects.length} project{projects.length !== 1 ? 's' : ''} total
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => setDialogOpen(true)}>
          New Project
        </Button>
      </Box>

      {/* Toolbar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search projects..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
            ),
          }}
          sx={{ minWidth: 240 }}
        />
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {STATUS_OPTIONS.map(opt => (
            <Chip
              key={opt.value}
              label={opt.label}
              variant={statusFilter === opt.value ? 'filled' : 'outlined'}
              color={statusFilter === opt.value ? 'primary' : 'default'}
              onClick={() => setStatusFilter(opt.value)}
              size="small"
            />
          ))}
        </Box>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Client</InputLabel>
          <Select
            value={clientFilter}
            label="Client"
            onChange={(e) => setClientFilter(e.target.value)}
          >
            <MenuItem value="all">All Clients</MenuItem>
            {clients.map(c => (
              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Project Grid */}
      {filteredProjects.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <FolderOpen sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No projects found
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
            {projects.length === 0 ? 'Create your first project to get started.' : 'Try adjusting your filters.'}
          </Typography>
          {projects.length === 0 && (
            <Button variant="contained" startIcon={<Add />} onClick={() => setDialogOpen(true)}>
              Create Project
            </Button>
          )}
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredProjects.map(project => {
            const client = project.clientId ? clientMap[project.clientId] : null;
            const budgetHours = project.budgetHours ? parseFloat(project.budgetHours) : 0;
            return (
              <Grid item xs={12} sm={6} md={4} key={project.id}>
                <Card sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid rgba(148, 163, 184, 0.08)',
                  transition: 'all 0.2s ease',
                  overflow: 'hidden',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' },
                }}>
                  {/* Color bar */}
                  <Box sx={{ height: 4, bgcolor: project.color }} />
                  <CardActionArea
                    onClick={() => navigate(`/projects/${project.id}`)}
                    sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                  >
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      {/* Code + Status */}
                      <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center' }}>
                        <Chip label={project.code} size="small" sx={{ fontWeight: 600, fontFamily: 'monospace' }} />
                        <Chip
                          label={project.status}
                          size="small"
                          color={STATUS_COLORS[project.status]}
                          variant="outlined"
                        />
                        {project.isBillable && (
                          <AttachMoney sx={{ fontSize: 16, color: '#10b981', ml: 'auto' }} />
                        )}
                      </Box>

                      {/* Name */}
                      <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5, lineHeight: 1.3 }}>
                        {project.name}
                      </Typography>

                      {/* Client */}
                      {client && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          {client.name}
                        </Typography>
                      )}

                      {/* Budget */}
                      {budgetHours > 0 && (() => {
                        const budget = budgetMap[project.id];
                        const usedHours = budget?.usedHours || 0;
                        const pct = Math.min((usedHours / budgetHours) * 100, 100);
                        return (
                          <Box sx={{ mt: 'auto', mb: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">Budget</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {usedHours.toFixed(1)} / {budgetHours}h
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={pct}
                              sx={{
                                height: 6, borderRadius: 3, bgcolor: 'rgba(148,163,184,0.1)',
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: pct > 90 ? '#ef4444' : pct > 75 ? '#f59e0b' : '#10b981',
                                },
                              }}
                            />
                          </Box>
                        );
                      })()}

                      {/* Tags */}
                      {project.tags && project.tags.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                          {project.tags.slice(0, 3).map(tag => (
                            <Chip key={tag} label={tag} size="small" variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 22 }} />
                          ))}
                          {project.tags.length > 3 && (
                            <Chip label={`+${project.tags.length - 3}`} size="small"
                              sx={{ fontSize: '0.7rem', height: 22 }} />
                          )}
                        </Box>
                      )}

                      {/* Footer */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 'auto', pt: 2, borderTop: '1px solid rgba(148,163,184,0.08)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CalendarToday sx={{ fontSize: 14, color: 'text.disabled' }} />
                          <Typography variant="caption" color="text.disabled">
                            {format(new Date(project.createdAt), 'MMM d, yyyy')}
                          </Typography>
                        </Box>
                        {project.assignedUserIds && project.assignedUserIds.length > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <People sx={{ fontSize: 14, color: 'text.disabled' }} />
                            <Typography variant="caption" color="text.disabled">
                              {project.assignedUserIds.length}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* New Project Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Project</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
          <TextField
            label="Project Name"
            required
            fullWidth
            value={form.name}
            onChange={(e) => handleNameChange(e.target.value)}
            autoFocus
          />
          <TextField
            label="Project Code"
            fullWidth
            value={form.code}
            onChange={(e) => setForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
            helperText="Auto-generated from name. You can customize it."
            inputProps={{ style: { fontFamily: 'monospace', fontWeight: 600 } }}
          />
          <Autocomplete
            options={clients}
            getOptionLabel={(option) => option.name}
            value={clients.find(c => c.id === form.clientId) || null}
            onChange={(_, val) => setForm(prev => ({ ...prev, clientId: val?.id || '' }))}
            renderInput={(params) => <TextField {...params} label="Client" />}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={form.description}
            onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
          />

          {/* Color picker */}
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Project Color</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {PROJECT_COLORS.map(color => (
                <Box
                  key={color}
                  onClick={() => setForm(prev => ({ ...prev, color }))}
                  sx={{
                    width: 32, height: 32, borderRadius: '50%', bgcolor: color, cursor: 'pointer',
                    border: form.color === color ? '3px solid white' : '3px solid transparent',
                    boxShadow: form.color === color ? `0 0 0 2px ${color}` : 'none',
                    transition: 'all 0.15s ease',
                    '&:hover': { transform: 'scale(1.15)' },
                  }}
                />
              ))}
            </Box>
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={form.isBillable}
                onChange={(e) => setForm(prev => ({ ...prev, isBillable: e.target.checked }))}
              />
            }
            label="Billable"
          />

          {form.isBillable && (
            <TextField
              label="Hourly Rate"
              type="number"
              fullWidth
              value={form.hourlyRate}
              onChange={(e) => setForm(prev => ({ ...prev, hourlyRate: e.target.value }))}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          )}

          <TextField
            label="Budget Hours"
            type="number"
            fullWidth
            value={form.budgetHours}
            onChange={(e) => setForm(prev => ({ ...prev, budgetHours: e.target.value }))}
            helperText="Leave empty for no budget limit."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!form.name.trim() || submitting}
          >
            {submitting ? 'Creating...' : 'Create Project'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProjectsPage;
